import { Common, color } from '@stockbit/ui';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import VisibilitySensor from 'react-visibility-sensor';

import SecondarySpinner from 'global/Loading/SecondarySpinner';
import debounce from 'lodash.debounce';
import { selectors as profileSelector } from 'lib/entities/profile';
import {
  PAGE_SOURCE,
  PAGE_SOURCE_INDEX,
} from 'features/user/analytics/ProfileAnalytics';
import { ModalFullscreen } from '../styled';
import slice, { effects, selectors } from '../../slice';
import UserItem from './UserItem';
import { ModalFollowSearchInput, UserItemContainer } from './styled';

const { Text } = Common;

type ModalMutualFollowerProps = {
  visible: boolean;
  onCancel: () => void;
  username: string;
  userId: number;
  symbol?: never;
  [key: string]: any;
};

const pageSourceIndex = PAGE_SOURCE_INDEX.findIndex(
  (sourceIndex) => sourceIndex === 'Followers',
);

function ModalMutualFollower({
  username,
  userId,
  symbol,
  visible,
  onCancel,
  ...props
}: ModalMutualFollowerProps) {
  const dispatch = useDispatch();
  const visibilityRef = useRef<HTMLDivElement>(null);
  const {
    userMutualFollower,
    isLoadingMutualFollower: isLoading,
    mutualFollowerPagination: pagination,
  } = useSelector(selectors);
  const { more, page, search: keyword } = pagination;
  const { data: dataProfile } = useSelector(profileSelector);

  const [isSensorVisible, setSensorVisible] = useState(false);

  const handleSensorChange = (isVisible = false) => {
    setSensorVisible(isVisible);
  };

  const handleGetMutualFollowers = useCallback(async () => {
    if (!isSensorVisible || !more || isLoading) return;

    dispatch(
      effects.getMutualFollower({
        userid: userId,
        keyword,
        page,
        limit: 10,
      }),
    );
  }, [isSensorVisible, isLoading, page, more, keyword, userId]);

  useEffect(() => {
    if (visible) {
      handleGetMutualFollowers();
    } else {
      // dispatch(slice.actions.resetMutualFollowerState());
    }
  }, [visible, handleGetMutualFollowers]);

  const searchFollowers = useCallback(
    debounce((search?: string) => {
      dispatch(slice.actions.searchMutualFollower(search));
      setSensorVisible(true);
    }, 500),
    [],
  );

  const handleSuccessFollowUnfollow = useCallback(() => {
    dispatch(
      effects.getMutualFollower({
        userid: userId,
        page: 1,
        limit: 10,
      }),
    );
  }, [userId]);
  const modalTitle = username || symbol;

  const followers =
    userMutualFollower[userId]?.data?.map((x) => ({
      ...x,
      avatar: x.user_avatar,
      is_followed: x.follow,
      fullname: x.user_fullname,
      id: Number(x.userid),
      username: x.username,
      is_verified: x.is_verified,
      real_phone: x.real_phone,
      country: x.country,
    })) || [];

  return (
    <ModalFullscreen
      {...props}
      noBorder
      visible={visible}
      onCancel={onCancel}
      footer={null}
      type="fullscreen"
      maskClosable={false}
      title={
        <Text size="20px" family="bold" align="center" color={color.suvaGray}>
          {modalTitle} is followed by
        </Text>
      }
    >
      <ModalFollowSearchInput
        placeholder="Search"
        onChange={({ target: { value } }) =>
          searchFollowers(value || undefined)
        }
        onClear={() => dispatch(slice.actions.searchFollowers())}
      />
      <UserItemContainer ref={visibilityRef}>
        {followers.map((follower) => (
          <UserItem
            pageSource={PAGE_SOURCE.followers}
            pageSourceIndex={pageSourceIndex}
            user={follower}
            key={follower.id}
            activeUserId={Number(dataProfile?.id)}
            onSuccessFollowUnfollow={handleSuccessFollowUnfollow}
          />
        ))}
        <SecondarySpinner isLoading={isLoading} />
        <VisibilitySensor
          containment={visibilityRef.current}
          onChange={handleSensorChange}
          intervalCheck
          scrollCheck
        >
          <div>&nbsp;</div>
        </VisibilitySensor>
      </UserItemContainer>
    </ModalFullscreen>
  );
}

export default ModalMutualFollower;
